'use client';

import { useMemo } from 'react';

import { useAui, useAuiState } from '@assistant-ui/react';

import {
  isRunning,
  isSuppressedTool,
  isTextPart,
  isToolCallPart,
} from '@/components/assistant-ui/message-part-utils';
import { classifyToolPart } from '@/components/assistant-ui/thread/activity-trail-helpers';
import { genericTrailLabelKey } from '@/components/assistant-ui/thread/trail-generic-label';
import type {
  AnyToolCallPart,
  ProgressStepStatus,
  TrailStepRecord,
} from '@/components/assistant-ui/thread/trail-records';
import {
  resolveTrailVariants,
  tryCall,
  type TrailVariantOverrides,
} from '@/components/assistant-ui/thread/trail-variants';
import type {
  TrailLabelContext,
  TrailToolConfig,
} from '@/components/assistant-ui/trail-tool-config';
import { streamingStepId } from '@/lib/assistant-cloud-local/step-ids';
import { GENERIC_TOOL_ICONS, type ToolIconConfig } from '@/lib/constants/tool-ui-icons';
import { pickInSequence } from '@/lib/utils/pick-by-hash';

export function statusRank(s: ProgressStepStatus): number {
  return s === 'in-progress' || s === 'awaiting-approval'
    ? 3
    : s === 'pending'
      ? 2
      : s === 'completed'
        ? 1
        : 0;
}

/** Folds parallel tool calls sharing the same compound merge key into a single record.
 * The first occurrence keeps its id/label/icons; subsequent ones promote status and resultStatus.
 * Mutates `icons` in place by removing entries for merged-away call IDs. */
export function mergeToolRecords({
  records,
  mergeKeys,
  icons,
}: {
  records: TrailStepRecord[];
  mergeKeys: Map<string, string>;
  icons: Map<string, ToolIconConfig[]>;
}): { stepRecords: TrailStepRecord[]; iconsByStepId: Map<string, ToolIconConfig[]> } {
  if (mergeKeys.size === 0) return { stepRecords: records, iconsByStepId: icons };

  // The first occurrence wins the id/label/icons; subsequent ones fold their
  // status and resultStatus into it.
  const mergeTargets = new Map<string, TrailStepRecord>();
  const merged: TrailStepRecord[] = [];
  for (const record of records) {
    const groupKey = record.kind === 'tool' ? mergeKeys.get(record.id) : undefined;
    if (!groupKey) {
      merged.push(record);
      continue;
    }
    const target = mergeTargets.get(groupKey);
    if (!target) {
      const copy = { ...record };
      mergeTargets.set(groupKey, copy);
      merged.push(copy);
    } else {
      if (statusRank(record.status) > statusRank(target.status)) target.status = record.status;
      if (record.resultStatus === 'results') {
        target.resultStatus = 'results';
      } else if (record.resultStatus === 'failed' && target.resultStatus !== 'results') {
        target.resultStatus = 'failed';
      } else if (record.resultStatus === 'empty' && target.resultStatus == null) {
        target.resultStatus = 'empty';
      }
      icons.delete(record.id);
    }
  }
  return { stepRecords: merged, iconsByStepId: icons };
}

interface UseStepRecordsArgs {
  isMessageRunning: boolean;
  trailConfig: TrailVariantOverrides | undefined;
  getTrailConfig: ((toolName: string) => TrailToolConfig | undefined) | undefined;
  t: (key: string, values?: Record<string, string | number>) => string;
  locale: string;
  /**
   * Stable-reference map of resolved IATA → city-name lookups, taken
   * directly from the destinations cache. Identity changes only when the
   * cache updates, so it's safe to include in the memo deps without
   * thrashing on every parent render.
   */
  destinationsMap?: Record<string, string>;
}

interface StepRecordsBundle {
  stepRecords: TrailStepRecord[];
  iconsByStepId: Map<string, ToolIconConfig[]>;
}

/**
 * Walks `message.parts` and produces ordered tool/text step records + a
 * rotating-icon map keyed by step id. Reasoning parts are skipped — they
 * surface via auto-generated thinking-gap rows in `buildTrailTimeline`.
 *
 * Keyed by a structural fingerprint so streaming token-level changes don't
 * trigger a rebuild on every chunk — only structural transitions do.
 */
export function useStepRecords({
  isMessageRunning,
  trailConfig,
  getTrailConfig,
  t,
  locale,
  destinationsMap,
}: UseStepRecordsArgs): StepRecordsBundle {
  const api = useAui();
  const fingerprint = useAuiState(({ message }) => {
    const parts = message?.parts as
      | readonly {
          type: string;
          toolCallId?: string;
          result?: unknown;
          isError?: boolean;
          status?: { type: string };
        }[]
      | undefined;
    if (!parts?.length) return '';
    let fp = '';
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (fp) fp += '|';
      if (part.type === 'tool-call') {
        const hasArgs = (part as { args?: unknown }).args !== undefined;
        const approval = (part as { approval?: { approved?: boolean; resolution?: string } })
          .approval;
        const approvalFp = approval
          ? `:${approval.approved ?? 'p'}:${approval.resolution ?? ''}`
          : '';
        fp += `tc:${part.toolCallId}:${hasArgs ? 1 : 0}:${
          part.result !== undefined ? 1 : 0
        }:${part.isError ? 1 : 0}${approvalFp}`;
      } else if (part.type === 'text') {
        fp += `tx:${part.status?.type ?? 'static'}`;
      } else {
        fp += `o:${part.type}`;
      }
    }
    return fp;
  });

  return useMemo<StepRecordsBundle>(() => {
    // `fingerprint` is the structural-change trigger that drives this memo;
    // referenced here so `react-hooks/exhaustive-deps` keeps it in deps.
    void fingerprint;
    const records: TrailStepRecord[] = [];
    const icons = new Map<string, ToolIconConfig[]>();
    const mergeKeys = new Map<string, string>(); // toolCallId → "toolName::mergeKey"
    const toolCallOrdinals = new Map<string, number>(); // toolName → # calls seen so far

    const getDestinationName = destinationsMap
      ? (code: string) => destinationsMap[code] ?? code
      : undefined;
    const ctx: TrailLabelContext = { t, locale, getDestinationName };
    const variants = resolveTrailVariants({
      overrides: trailConfig,
      t,
    });
    const parts = (api.message.getState().content ?? []) as readonly unknown[];
    const lastIndex = parts.length - 1;
    let textIndex = 0;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (isToolCallPart(part)) {
        const toolPart = part as AnyToolCallPart;
        // Mastra-internal bookkeeping (e.g. updateWorkingMemory) shouldn't
        // surface as a trail step — same suppression list as MessageError.
        if (isSuppressedTool(toolPart.toolName)) continue;
        const status = classifyToolPart(toolPart);
        const toolCallOrdinal = toolCallOrdinals.get(toolPart.toolName) ?? 0;
        toolCallOrdinals.set(toolPart.toolName, toolCallOrdinal + 1);

        const toolTrailConfig = getTrailConfig?.(toolPart.toolName);
        // No per-tool config (tool unmapped, or tenant has no tool group):
        // surface a generic keyword-based row so the trail still represents the work.
        if (!toolTrailConfig) {
          records.push({
            id: toolPart.toolCallId,
            kind: 'tool',
            label: t(genericTrailLabelKey(toolPart.toolName)),
            status,
            toolName: toolPart.toolName,
          });
          icons.set(toolPart.toolCallId, GENERIC_TOOL_ICONS);
          continue;
        }
        // The trail row text stays the same across all statuses — the status
        // icon (spinner → ✓ → ✗) carries the state change. Fall back to the
        // static `${ns}.loading` translation when no dynamic getter is set.
        const label =
          tryCall(() =>
            toolTrailConfig.getLabel?.(toolPart.args, {
              ...ctx,
              toolCallId: toolPart.toolCallId,
              toolCallOrdinal,
            }),
          ) ?? t(`${toolTrailConfig.translationNamespace}.loading`);
        // Result classification: failed status overrides any extractor opinion;
        // for completed calls, defer to the per-tool extractor (which can flag
        // "successfully empty"). Skip extraction while the call is still in
        // progress — `result` is undefined and counts would be misleading.
        const resultStatus =
          status === 'failed'
            ? ('failed' as const)
            : status === 'completed'
              ? tryCall(() => toolTrailConfig.getResultStatus?.(toolPart.args, toolPart.result))
              : undefined;
        records.push({
          id: toolPart.toolCallId,
          kind: 'tool',
          label,
          status,
          toolBucket: toolTrailConfig.bucket,
          toolName: toolPart.toolName,
          resultStatus,
        });
        if (toolTrailConfig.icons && toolTrailConfig.icons.length > 0) {
          icons.set(toolPart.toolCallId, toolTrailConfig.icons);
        }
        const mergeKey = tryCall(() => toolTrailConfig.getMergeKey?.(toolPart.args));
        if (mergeKey) mergeKeys.set(toolPart.toolCallId, `${toolPart.toolName}::${mergeKey}`);
        continue;
      }

      if (isTextPart(part)) {
        // One record per text part — keeps record ids aligned with the step
        // keys the timing reducer assigns (`text-0`, `text-1`, ...). Don't
        // collapse contiguous text parts: when the model emits a placeholder
        // text segment before a tool call and the real response after,
        // collapsing would orphan the second part's timing and the trailing
        // gap row would absorb the entire response-drafting duration.
        const partTextIndex = textIndex++;
        const isThisStreaming =
          isRunning(part as { status?: { type: string } }) || (isMessageRunning && i === lastIndex);
        const text = (part as { text?: string }).text ?? '';
        // Skip an already-completed empty text part — pure noise (assistant
        // started a text segment then closed it without writing anything).
        // Keep streaming parts even when empty so the "drafting" row appears
        // on the very first frame after `text-start`.
        if (!text && !isThisStreaming) continue;
        // Generic streaming label — bucket-agnostic by design (the model's
        // mid-stream text isn't guaranteed to relate to the prior tool, so
        // a generic phrase reads more honestly than guessing context).
        const streamingLabel = pickInSequence({
          items: variants.streaming,
          seed: 'streaming',
          index: partTextIndex,
        });
        records.push({
          id: streamingStepId(partTextIndex),
          kind: 'text',
          status: isThisStreaming ? 'in-progress' : 'completed',
          label: streamingLabel,
          awaitsContent: isThisStreaming && !text,
        });
      }
    }

    // Sequential-order invariant: steps run in order, so a step CANNOT still be in-progress once
    // any LATER step has resolved. Replayed/rehydrated messages violate this — a burst replay can
    // leave an earlier tool part without a result while later parts already carry theirs, which
    // rendered as a stuck spinner on step 1 (or a wall of concurrent spinners). Demote any
    // in-progress step that is followed by a resolved one; at most the final unresolved step
    // (the genuinely-active one) keeps spinning.
    const RESOLVED: ReadonlySet<ProgressStepStatus> = new Set([
      'completed',
      'failed',
      'cancelled',
      'awaiting-approval',
    ]);
    let laterResolvedExists = false;
    for (let i = records.length - 1; i >= 0; i--) {
      const record = records[i];
      if (record.status === 'in-progress' && laterResolvedExists) {
        record.status = 'completed';
      } else if (RESOLVED.has(record.status)) {
        laterResolvedExists = true;
      }
    }

    return mergeToolRecords({ records, mergeKeys, icons });
  }, [fingerprint, isMessageRunning, trailConfig, getTrailConfig, api, t, locale, destinationsMap]);
}
